import type { AirRingColors, RingSizes } from 'content/products/ring';
import { RingSizes as RingSizesEnum } from 'content/products/ring';
import { UhxShopifyMap } from 'content/products/uhx';
import type { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import type { TradeInDevice } from 'content/products/tradeIn';
import {
  trackEvent,
  trackFunnelEvent,
  pushEcommerceEvent,
  type EcommerceItem,
  type FunnelEventPayload,
} from 'components/shared/UserAnalytics/funnelEvents';
import type { addonsType } from 'components/ring-air/utils/types';
import type { AeEmirate } from 'components/ring-air/AeDeliveryEta/emirates';
import type { AeDeliveryWindowState } from 'components/ring-air/AeDeliveryEta/deliveryWindow';

export type CtaPlacement = 'summary' | 'bottom_bar';
export type RingBuyCta = 'add_to_cart' | 'buy_now';

// GTM dataLayer (camelCase; GA4 recommended ecommerce names stay snake_case)
export const RING_BUY_GTM_EVENTS = {
  VIEW_ITEM: 'view_item',
  DEFAULTS_APPLIED: 'ringBuyDefaultsApplied',
  CTA_CLICK: 'ringBuyCtaClick',
  ADD_TO_CART: 'add_to_cart',
  ADD_TO_CART_FAILED: 'ringBuyAddToCartFailed',
  SECTION_VIEWED: 'ringBuySectionViewed',
  COLOR_SELECTED: 'ringColorSelected',
  SIZE_SELECTED: 'ringSizeSelected',
  SIZE_MANUAL_TOGGLE: 'ringSizeManualToggle',
  SIZE_GUIDE_LINK: 'ringSizeGuideLinkClick',
  UHX_CHANGED: 'uhxSelectionChanged',
  UHX_INFO_OPEN: 'uhxInfoModalOpen',
  UHX_BONUS_SHOWN: 'uhxBonusBannerShown',
  TRADE_IN_OPEN: 'tradeInModalOpen',
  TRADE_IN_DEVICE: 'tradeInDeviceSelected',
  TRADE_IN_CONFIRMED: 'tradeInConfirmed',
  TRADE_IN_CLOSED: 'tradeInModalClosed',
  TRADE_IN_REMOVED: 'tradeInRemoved',
  CAROUSEL_NAV: 'ringCarouselNav',
  REAL_LIFE_TAB: 'realLifeImagesTabClick',
  HEROES_OFFER: 'heroesOfferClick',
  BOTTOM_BAR_TOTAL: 'bottomBarTotalTap',
} as const;

// Vercel Analytics (snake_case). Option events use the snake_case form of
// their GTM name automatically (trackEvent); these four differ from it.
export const RING_BUY_ANALYTICS_EVENTS = {
  VIEW_ITEM: 'ring_buy_view_item',
  CTA_CLICK: 'ring_buy_cta_click',
  ADD_TO_CART: 'ring_buy_add_to_cart',
  ADD_TO_CART_FAILED: 'ring_buy_add_to_cart_failed',
} as const;

export type RingSelectionSnapshot = {
  store: ShopifyStore | null;
  currency: string;
  color: AirRingColors | null | undefined;
  size: RingSizes | null | undefined;
  sizeMethod: 'sizing_kit' | 'numeric' | null;
  sizeDefaultKept: boolean;
  sizeTouched: boolean;
  uhxPlan: 'Year1' | 'Year2' | 'none' | 'unset';
  uhxDefaultKept: boolean;
  uhxTouched: boolean;
  tradeIn: boolean;
  tradeInDevice: TradeInDevice | null;
  tradeInCredit: number;
  variantId: string | undefined;
  price: number;
  effectivePrice: number;
  leanFlow: boolean;
  /** AE only: emirate chosen in the delivery card; undefined elsewhere and
   * until a selection is made, so non-AE payloads are unchanged. */
  emirate: AeEmirate | undefined;
  /** AE only: the delivery-window promise in effect when the snapshot was
   * built. Undefined until the buyer selects an emirate (and when the kill
   * switch is off), so an order never records a promise that was not shown. */
  aeDeliveryWindow: AeDeliveryWindowState | undefined;
};

export const uhxPlanOf = (
  uhxCart: addonsType,
): RingSelectionSnapshot['uhxPlan'] =>
  uhxCart === 'Year1' || uhxCart === 'Year2'
    ? uhxCart
    : uhxCart === false
      ? 'none'
      : 'unset';

/** `RingSizes.OPEN` is the numeric 99; report it as 'open'. */
export const sizeLabel = (
  size: RingSizes | null | undefined,
): RingSizes | 'open' | undefined =>
  size == null ? undefined : size === RingSizesEnum.OPEN ? 'open' : size;

export const sizeMethodOf = (
  size: RingSizes | null | undefined,
): RingSelectionSnapshot['sizeMethod'] =>
  !size ? null : size === RingSizesEnum.OPEN ? 'sizing_kit' : 'numeric';

export const snapshotPayload = (
  snapshot: RingSelectionSnapshot,
): FunnelEventPayload => ({
  store: snapshot.store ?? undefined,
  currency: snapshot.currency,
  color: snapshot.color ?? undefined,
  size: sizeLabel(snapshot.size),
  sizeMethod: snapshot.sizeMethod ?? undefined,
  sizeDefaultKept: snapshot.sizeDefaultKept,
  sizeTouched: snapshot.sizeTouched,
  uhxPlan: snapshot.uhxPlan,
  uhxDefaultKept: snapshot.uhxDefaultKept,
  uhxTouched: snapshot.uhxTouched,
  tradeIn: snapshot.tradeIn,
  tradeInDevice: snapshot.tradeInDevice ?? undefined,
  tradeInCredit: snapshot.tradeInCredit,
  variantId: snapshot.variantId,
  price: snapshot.price,
  effectivePrice: snapshot.effectivePrice,
  leanFlow: snapshot.leanFlow,
  emirate: snapshot.emirate,
  aeDeliveryWindow: snapshot.aeDeliveryWindow,
});

/** GA4 `items[]` for the current selection: ring, sizing kit, UHX plan. */
export function buildRingEcommerceItems(
  snapshot: RingSelectionSnapshot,
  sizingKitVariantId?: string,
): EcommerceItem[] {
  const items: EcommerceItem[] = [];
  if (snapshot.variantId) {
    items.push({
      item_id: snapshot.variantId,
      item_name: 'Ultrahuman Ring AIR',
      item_variant: [snapshot.color, sizeLabel(snapshot.size)]
        .filter(Boolean)
        .join(' / '),
      item_category: 'ring',
      price: snapshot.price,
      quantity: 1,
    });
  }
  if (snapshot.sizeMethod === 'sizing_kit' && sizingKitVariantId) {
    items.push({
      item_id: sizingKitVariantId,
      item_name: 'Ring AIR Sizing Kit',
      item_category: 'sizing_kit',
      price: 0,
      quantity: 1,
    });
  }
  if (
    snapshot.store &&
    (snapshot.uhxPlan === 'Year1' || snapshot.uhxPlan === 'Year2')
  ) {
    const plan = UhxShopifyMap[snapshot.store]?.[snapshot.uhxPlan];
    if (plan?.variantId) {
      items.push({
        item_id: String(plan.variantId),
        item_name: `UltrahumanX ${
          snapshot.uhxPlan === 'Year1' ? '1' : '2'
        } Year`,
        item_category: 'uhx',
        price: plan.price,
        quantity: 1,
      });
    }
  }
  return items;
}

export function trackRingBuyViewItem(
  snapshot: RingSelectionSnapshot,
  items: EcommerceItem[],
) {
  pushEcommerceEvent(
    'view_item',
    { currency: snapshot.currency, value: snapshot.effectivePrice, items },
    snapshotPayload(snapshot),
  );
  trackFunnelEvent(
    null,
    RING_BUY_ANALYTICS_EVENTS.VIEW_ITEM,
    snapshotPayload(snapshot),
  );
}

export function trackRingBuyCtaClick(
  snapshot: RingSelectionSnapshot,
  cta: RingBuyCta,
  placement: CtaPlacement,
) {
  trackFunnelEvent(
    RING_BUY_GTM_EVENTS.CTA_CLICK,
    RING_BUY_ANALYTICS_EVENTS.CTA_CLICK,
    { ...snapshotPayload(snapshot), cta, placement },
  );
}

export function trackRingBuyAddToCart(
  snapshot: RingSelectionSnapshot,
  items: EcommerceItem[],
  cta: RingBuyCta,
  placement: CtaPlacement,
) {
  pushEcommerceEvent(
    'add_to_cart',
    { currency: snapshot.currency, value: snapshot.effectivePrice, items },
    { cta, placement, ...snapshotPayload(snapshot) },
  );
  trackFunnelEvent(null, RING_BUY_ANALYTICS_EVENTS.ADD_TO_CART, {
    ...snapshotPayload(snapshot),
    cta,
    placement,
  });
}

export function trackRingBuyAddToCartFailed(
  snapshot: RingSelectionSnapshot,
  cta: RingBuyCta,
  placement: CtaPlacement,
  reason: 'blocked' | 'api_error' | 'exception' | 'empty_items',
  message?: string,
) {
  trackFunnelEvent(
    RING_BUY_GTM_EVENTS.ADD_TO_CART_FAILED,
    RING_BUY_ANALYTICS_EVENTS.ADD_TO_CART_FAILED,
    { ...snapshotPayload(snapshot), cta, placement, reason, message },
  );
}

/** Option-level events: GTM (camelCase) + Vercel (snake_case of the same name). */
export function trackRingBuyOption(
  event: (typeof RING_BUY_GTM_EVENTS)[keyof typeof RING_BUY_GTM_EVENTS],
  payload: FunnelEventPayload = {},
) {
  trackEvent(event, payload);
}
